import React, { useState, useCallback, useMemo } from 'react';
import { useSearchParams, Link, useParams } from 'react-router-dom';
import { FiSearch, FiPlus } from 'react-icons/fi';
import * as wasm from 'sched';
import { useDatabase } from '../../hooks/useDatabase';
import NativeTaskForm from '../../components/forms/NativeTaskForm';
import NativePersonForm from '../../components/forms/NativePersonForm';
import NativeTeamForm from '../../components/forms/NativeTeamForm';
import NativeOutcomeForm from '../../components/forms/NativeOutcomeForm';
import NativeDecisionForm from '../../components/forms/NativeDecisionForm';
import NativeProblemForm from '../../components/forms/NativeProblemForm';
import NativeProductForm from '../../components/forms/NativeProductForm';
import NativeStakeholderForm from '../../components/forms/NativeStakeholderForm';
import NativeResourceForm from '../../components/forms/NativeResourceForm';
import NativeResourceGroupForm from '../../components/forms/NativeResourceGroupForm';

interface SearchResult {
  entity_type: string;
  id: string;
  title: string;
}

type EntityType = 'task' | 'person' | 'team' | 'product' | 'outcome' | 'decision' | 'stakeholder' | 'problem' | 'resource' | 'resourceGroup' | null;

const ENTITY_TYPES: { key: EntityType & string; label: string; subtitle: string; color: string }[] = [
  { key: 'task', label: 'Task', subtitle: 'A unit of work to be done', color: 'text-cm-accent-blue' },
  { key: 'person', label: 'Person', subtitle: 'Someone who does work', color: 'text-cm-accent-green' },
  { key: 'team', label: 'Team', subtitle: 'A group of people', color: 'text-cm-accent-teal' },
  { key: 'product', label: 'Product', subtitle: 'A long-running concern with features and users', color: 'text-cm-accent-orange' },
  { key: 'outcome', label: 'Outcome', subtitle: 'A goal or deliverable', color: 'text-cm-accent-purple' },
  { key: 'decision', label: 'Decision', subtitle: 'A choice that gates work', color: 'text-cm-accent-gold' },
  { key: 'problem', label: 'Problem', subtitle: 'Something to be solved', color: 'text-cm-accent-red' },
  { key: 'stakeholder', label: 'Stakeholder', subtitle: 'An interested party', color: 'text-cm-accent-pink' },
  { key: 'resource', label: 'Resource', subtitle: 'A tool, facility, or other bookable asset', color: 'text-cm-accent-teal' },
  { key: 'resourceGroup', label: 'Resource Group', subtitle: 'A collection of resources', color: 'text-cm-accent-teal' },
];

const TYPE_COLOR: Record<string, string> = {
  Task: 'text-cm-accent-blue',
  Person: 'text-cm-accent-green',
  Team: 'text-cm-accent-teal',
  Project: 'text-cm-accent-orange',
  Outcome: 'text-cm-accent-purple',
  Decision: 'text-cm-accent-gold',
  Stakeholder: 'text-cm-accent-pink',
  Resource: 'text-cm-accent-teal',
  ResourceGroup: 'text-cm-accent-teal',
};

const TYPE_PATH: Record<string, string> = {
  Task: 'tasks',
  Person: 'people',
  Team: 'teams',
  Project: 'products',
  Outcome: 'outcomes',
  Decision: 'decisions',
  Problem: 'problems',
  Stakeholder: 'stakeholders',
  Resource: 'resources',
  ResourceGroup: 'resource-groups',
};

const DataExplorePage: React.FC = () => {
  const { workspaceId } = useParams();
  const { applyCommand, database, safeData } = useDatabase();
  const [showTypeSelect, setShowTypeSelect] = useState(false);
  const [activeForm, setActiveForm] = useState<EntityType>(null);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [inputValue, setInputValue] = useState(searchParams.get('q') ?? '');
  const submittedQuery = searchParams.get('q') ?? '';
  const isDirty = inputValue !== submittedQuery;

  const projects = useMemo(() => {
    if (!database) return [];
    const projectMap = database.projects();
    return Object.entries(projectMap).map(([id, p]) => ({ id, name: (p as any).name() }));
  }, [database]);

  const submitSearch = useCallback(async (q: string) => {
    if (!safeData) return;
    const next = new URLSearchParams(searchParams);
    if (q.trim()) {
      next.set('q', q);
    } else {
      next.delete('q');
    }
    setSearchParams(next, { replace: true });

    setIsSearching(true);
    try {
      const res: SearchResult[] = await wasm.search_all(safeData, q, 100);
      setResults(res);
    } catch (err) {
      console.error('search_all failed', err);
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [safeData, searchParams, setSearchParams]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      submitSearch(inputValue);
    }
  };

  const handleTypeSelect = (type: EntityType) => {
    setShowTypeSelect(false);
    setActiveForm(type);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-mono font-semibold text-cm-text-primary">Explore</h1>
          <p className="mt-1 text-sm text-cm-text-secondary">
            Search and find anything across your workspace — tasks, people, teams, projects, outcomes, and more.
          </p>
        </div>
        <button
          onClick={() => setShowTypeSelect(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-cm-accent-orange text-cm-bg-deep font-mono text-sm font-medium hover:opacity-90 transition-opacity"
        >
          <FiPlus className="w-4 h-4" />
          Create
        </button>
      </div>

      {/* Search input + results */}
      <div className="rounded-xl border border-cm-border-subtle bg-cm-bg-surface p-6">
        <div className="flex items-center gap-3 mb-4">
          <FiSearch className="w-5 h-5 text-cm-accent-blue" />
          <h2 className="font-mono font-medium text-cm-text-primary">Search</h2>
        </div>
        <div className="relative w-full">
          <input
            type="text"
            className="w-full pl-3 pr-10 py-2 border border-cm-border-subtle rounded-lg bg-cm-bg-elevated text-cm-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Search by name... (press Enter)"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button
            onClick={() => submitSearch(inputValue)}
            className={`absolute inset-y-0 right-0 flex items-center pr-3 transition-colors ${
              isDirty ? 'text-cm-accent-blue cursor-pointer' : 'text-cm-text-muted pointer-events-none'
            }`}
          >
            <FiSearch className={isDirty ? 'w-5 h-5' : ''} />
          </button>
        </div>

        {/* Results table */}
        <div className="mt-4">
          {isSearching ? (
            <p className="text-cm-text-muted text-sm font-mono py-4 text-center">Searching...</p>
          ) : results.length === 0 ? (
            <div className="flex items-center justify-center h-32 border border-dashed border-cm-border-mid rounded-lg">
              <p className="text-cm-text-muted text-sm font-mono">
                {submittedQuery ? 'No results found' : 'Type to search across all entities'}
              </p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-cm-border-subtle">
                  <th className="text-left py-2 px-3 font-mono font-medium text-cm-text-secondary w-32">Type</th>
                  <th className="text-left py-2 px-3 font-mono font-medium text-cm-text-secondary">Title</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r) => {
                  const pathSegment = TYPE_PATH[r.entity_type];
                  const to = pathSegment ? `/workspaces/${workspaceId}/${pathSegment}/${r.id}` : undefined;
                  return to ? (
                    <tr key={`${r.entity_type}-${r.id}`} className="border-b border-cm-border-subtle hover:bg-cm-bg-hover transition-colors cursor-pointer">
                      <td colSpan={2} className="p-0">
                        <Link to={to} className="flex w-full">
                          <span className={`py-2 px-3 font-mono text-xs w-32 ${TYPE_COLOR[r.entity_type] ?? 'text-cm-text-secondary'}`}>
                            {r.entity_type}
                          </span>
                          <span className="py-2 px-3 text-cm-text-primary flex-1">{r.title}</span>
                        </Link>
                      </td>
                    </tr>
                  ) : (
                    <tr key={`${r.entity_type}-${r.id}`} className="border-b border-cm-border-subtle">
                      <td className={`py-2 px-3 font-mono text-xs ${TYPE_COLOR[r.entity_type] ?? 'text-cm-text-secondary'}`}>
                        {r.entity_type}
                      </td>
                      <td className="py-2 px-3 text-cm-text-primary">{r.title}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Type selection modal */}
      {showTypeSelect && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-cm-bg-deep/70" onClick={() => setShowTypeSelect(false)} />
          <div className="relative bg-cm-bg-surface border border-cm-border-mid rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
            <h3 className="font-mono font-semibold text-cm-text-primary mb-4">Create new...</h3>
            <div className="grid grid-cols-2 gap-3">
              {ENTITY_TYPES.map(({ key, label, subtitle, color }) => (
                <button
                  key={key}
                  onClick={() => handleTypeSelect(key)}
                  className="flex flex-col p-3 rounded-lg border border-cm-border-subtle bg-cm-bg-elevated hover:bg-cm-bg-hover hover:border-cm-border-mid transition-colors text-left"
                >
                  <span className={`font-mono text-sm font-medium ${color}`}>{label}</span>
                  <span className="text-xs text-cm-text-muted mt-0.5">{subtitle}</span>
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowTypeSelect(false)}
              className="mt-4 w-full text-center text-sm text-cm-text-muted hover:text-cm-text-primary transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Entity creation forms */}
      <NativeTaskForm isOpen={activeForm === 'task'} onClose={() => setActiveForm(null)} onSubmit={applyCommand} projects={projects} />
      <NativePersonForm isOpen={activeForm === 'person'} onClose={() => setActiveForm(null)} onSubmit={applyCommand} />
      <NativeTeamForm isOpen={activeForm === 'team'} onClose={() => setActiveForm(null)} onSubmit={applyCommand} />
      <NativeProductForm isOpen={activeForm === 'product'} onClose={() => setActiveForm(null)} onSubmit={applyCommand} />
      <NativeOutcomeForm isOpen={activeForm === 'outcome'} onClose={() => setActiveForm(null)} onSubmit={applyCommand} />
      <NativeDecisionForm isOpen={activeForm === 'decision'} onClose={() => setActiveForm(null)} onSubmit={applyCommand} />
      <NativeProblemForm isOpen={activeForm === 'problem'} onClose={() => setActiveForm(null)} onSubmit={applyCommand} />
      <NativeStakeholderForm isOpen={activeForm === 'stakeholder'} onClose={() => setActiveForm(null)} onSubmit={applyCommand} />
      <NativeResourceForm isOpen={activeForm === 'resource'} onClose={() => setActiveForm(null)} onSubmit={applyCommand} />
      <NativeResourceGroupForm isOpen={activeForm === 'resourceGroup'} onClose={() => setActiveForm(null)} onSubmit={applyCommand} />
    </div>
  );
};

export default DataExplorePage;
